import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const ALL_MODULES = [
  "overview", "inventory", "movements", "orders", "invoices", "facturapro",
  "shipments", "labeling", "check-invoices", "users", "product-mappings", "settings", "administration"
];

export const useModulePermissions = (userId: string | undefined, userRole: string) => {
  const { data: allowedModules = null, isLoading: loading } = useQuery<Set<string> | null>({
    queryKey: ["modulePermissions", userId, userRole],
    queryFn: async () => {
      if (!userId || !userRole) return new Set<string>();

      // Fetch role + user permissions in parallel
      const [{ data: roleData }, { data: userData }] = await Promise.all([
        (supabase as any).from("role_module_permissions").select("module_key, enabled").eq("role", userRole),
        (supabase as any).from("user_module_permissions").select("module_key, enabled").eq("user_id", userId),
      ]);

      const rolePerms: Record<string, boolean> = {};
      (roleData || []).forEach((r: any) => { rolePerms[r.module_key] = r.enabled; });

      const userOverrides: Record<string, boolean> = {};
      (userData || []).forEach((r: any) => { userOverrides[r.module_key] = r.enabled; });

      const allowed = new Set<string>();
      ALL_MODULES.forEach((m) => {
        const effective = m in userOverrides ? userOverrides[m] : (rolePerms[m] ?? false);
        if (effective) allowed.add(m);
      });

      return allowed;
    },
    enabled: !!userId && !!userRole,
    staleTime: 10 * 60 * 1000, // 10 min — permissions rarely change
    gcTime: 30 * 60 * 1000,
  });

  const hasAccess = useCallback((moduleKey: string): boolean => {
    if (loading) return false;
    if (allowedModules === null) return true; // error: fail open
    return allowedModules.has(moduleKey);
  }, [allowedModules, loading]);

  return { hasAccess, loading };
};
